import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DemoFarmSection } from './DemoFarmSection';
import { TestPacketsSection } from './TestPacketsSection';
import { ResultsSummary } from './ResultsSummary';
import { Instructions } from './Instructions';
import { CropSetup } from './CropSetup';
import { type CropConfig, type DemoFarmInputs, type TestPacketsInputs, type CalculationResults, type AreaUnit, type WeightUnit, DEFAULT_CROPS, CROP_COLOR_PALETTE, getDefaultUnits } from './types';
import { useCurrency } from '../../hooks/useCurrency';
import { useLocale } from '../../i18n/LocaleContext';

declare global {
  interface Window {
    BayCrewAnalytics?: {
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      sessionId: string;
    };
  }
}

const LS_CROPS_KEY = 'spi-seed-crops';

function saveCropsToStorage(crops: CropConfig[]) {
  try {
    localStorage.setItem(LS_CROPS_KEY, JSON.stringify(crops));
  } catch {
    // localStorage unavailable
  }
}

function loadCropsFromStorage(): CropConfig[] | null {
  try {
    const stored = localStorage.getItem(LS_CROPS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Validate each crop has required fields
    const valid = parsed.filter(
      (c: unknown): c is CropConfig =>
        typeof c === 'object' && c !== null &&
        typeof (c as CropConfig).id === 'string' &&
        typeof (c as CropConfig).label === 'string' &&
        typeof (c as CropConfig).color === 'string' &&
        (c as CropConfig).id.length > 0 &&
        (c as CropConfig).label.length > 0
    );
    return valid.length > 0 ? valid : null;
  } catch {
    return null;
  }
}

const SS_KEY = 'spi-seed-calc';

interface SessionState {
  crops: CropConfig[];
  demoFarm: DemoFarmInputs;
  testPackets: TestPacketsInputs;
  currencyCode: string;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
}

function saveToSession(state: SessionState) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable
  }
}

function loadFromSession(): SessionState | null {
  try {
    const stored = sessionStorage.getItem(SS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || !Array.isArray(parsed.crops) || parsed.crops.length === 0) return null;
    return parsed as SessionState;
  } catch {
    return null;
  }
}

const DEFAULT_SEED_RATES: Record<string, number> = { maize: 5, bean: 5, cowpea: 10, eggplant: 0.1 };
const DEFAULT_SEED_COSTS: Record<string, number> = { maize: 7000, bean: 4600, cowpea: 5500, eggplant: 500000 };
const DEFAULT_PACKET_WEIGHTS: Record<string, number> = { maize: 0.2, bean: 0.1, cowpea: 0.1, eggplant: 0.002 };

function buildDemoFarmDefaults(crops: CropConfig[]): DemoFarmInputs {
  const evenPct = Math.floor(100 / crops.length);
  const remainder = 100 - evenPct * crops.length;
  const cropPercentages: Record<string, number> = {};
  const seedRatesKgPerHa: Record<string, number> = {};
  const seedCostsLocalPerKg: Record<string, number> = {};
  crops.forEach((c, i) => {
    cropPercentages[c.id] = evenPct + (i === 0 ? remainder : 0);
    seedRatesKgPerHa[c.id] = DEFAULT_SEED_RATES[c.id] ?? 5;
    seedCostsLocalPerKg[c.id] = DEFAULT_SEED_COSTS[c.id] ?? 5000;
  });
  return { farmHectares: 20, cropPercentages, seedRatesKgPerHa, seedCostsLocalPerKg };
}

function buildTestPacketsDefaults(crops: CropConfig[]): TestPacketsInputs {
  const packetWeightsKg: Record<string, number> = {};
  crops.forEach((c) => {
    packetWeightsKg[c.id] = DEFAULT_PACKET_WEIGHTS[c.id] ?? 0.1;
  });
  return {
    refugeeHouseholds: 1500,
    hostCommunityHouseholds: 500,
    avgPacketsPerRefugeeHousehold: 1.8,
    avgPacketsPerHostHousehold: 1.8,
    packetWeightsKg,
  };
}

const LS_GUIDE_KEY = 'spi-guide-visible';

function getInitialGuideVisible(): boolean {
  try {
    const stored = localStorage.getItem(LS_GUIDE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function parseCropRecord(param: string | null, cropIds: string[], fallback: Record<string, number>): Record<string, number> {
  if (!param) return fallback;
  const parts = param.split(',').map(Number);
  if (parts.length !== cropIds.length || parts.some(isNaN)) return fallback;
  const result: Record<string, number> = {};
  cropIds.forEach((id, i) => { result[id] = parts[i]; });
  return result;
}

function cropRecordToString(record: Record<string, number>, cropIds: string[]): string {
  return cropIds.map((id) => record[id] ?? 0).join(',');
}

function parseCropsFromParams(params: URLSearchParams): CropConfig[] | null {
  const cropsParam = params.get('crops');
  if (!cropsParam) return null;
  try {
    const pairs = cropsParam.split(';');
    return pairs.map((pair, i) => {
      const [label, color] = pair.split('|');
      return {
        id: label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label,
        color: color || CROP_COLOR_PALETTE[i % CROP_COLOR_PALETTE.length],
      };
    });
  } catch {
    return null;
  }
}

function cropsToParamString(crops: CropConfig[]): string {
  return crops.map((c) => `${c.label}|${c.color}`).join(';');
}

function parseStateFromParams(params: URLSearchParams, crops: CropConfig[]): {
  demoFarm: DemoFarmInputs;
  testPackets: TestPacketsInputs;
  currency: string | undefined;
  areaUnit: AreaUnit | undefined;
  weightUnit: WeightUnit | undefined;
} | null {
  if (!params.has('fh') && !params.has('rh') && !params.has('cur')) return null;

  const cropIds = crops.map((c) => c.id);
  const defaults = buildDemoFarmDefaults(crops);
  const testDefaults = buildTestPacketsDefaults(crops);

  const au = params.get('au');
  const wu = params.get('wu');

  return {
    demoFarm: {
      farmHectares: Number(params.get('fh')) || defaults.farmHectares,
      cropPercentages: parseCropRecord(params.get('cp'), cropIds, defaults.cropPercentages),
      seedRatesKgPerHa: parseCropRecord(params.get('sr'), cropIds, defaults.seedRatesKgPerHa),
      seedCostsLocalPerKg: parseCropRecord(params.get('sc'), cropIds, defaults.seedCostsLocalPerKg),
    },
    testPackets: {
      refugeeHouseholds: Number(params.get('rh')) || testDefaults.refugeeHouseholds,
      hostCommunityHouseholds: Number(params.get('hh')) || testDefaults.hostCommunityHouseholds,
      avgPacketsPerRefugeeHousehold: Number(params.get('rp')) || testDefaults.avgPacketsPerRefugeeHousehold,
      avgPacketsPerHostHousehold: Number(params.get('hp')) || testDefaults.avgPacketsPerHostHousehold,
      packetWeightsKg: parseCropRecord(params.get('pw'), cropIds, testDefaults.packetWeightsKg),
    },
    currency: params.get('cur') || undefined,
    areaUnit: (au === 'ha' || au === 'acres') ? au : undefined,
    weightUnit: (wu === 'kg' || wu === 'lbs' || wu === 'g') ? wu : undefined,
  };
}

export function SeedCalculator() {
  const { t } = useTranslation();
  const { country } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse crops from URL > sessionStorage > localStorage > defaults
  const sessionState = useMemo(() => loadFromSession(), []);
  const initialCrops = useMemo(() => parseCropsFromParams(searchParams) ?? sessionState?.crops ?? loadCropsFromStorage() ?? DEFAULT_CROPS, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [crops, setCrops] = useState<CropConfig[]>(initialCrops);
  const cropIds = useMemo(() => crops.map((c) => c.id), [crops]);

  // Parse initial state: URL params > sessionStorage > locale defaults
  const initialState = useMemo(() => parseStateFromParams(searchParams, initialCrops), []); // eslint-disable-line react-hooks/exhaustive-deps
  const localeDefaults = useMemo(() => getDefaultUnits(country.code), [country.code]);

  const [demoFarm, setDemoFarm] = useState<DemoFarmInputs>(
    initialState?.demoFarm ?? sessionState?.demoFarm ?? buildDemoFarmDefaults(initialCrops)
  );
  const [testPackets, setTestPackets] = useState<TestPacketsInputs>(
    initialState?.testPackets ?? sessionState?.testPackets ?? buildTestPacketsDefaults(initialCrops)
  );
  const [showInstructions, setShowInstructions] = useState(getInitialGuideVisible);
  const [copied, setCopied] = useState(false);

  const { currencyCode, setCurrencyCode: setRawCurrencyCode, exchangeRate, isLive, isLoading } = useCurrency(
    initialState?.currency ?? sessionState?.currencyCode
  );

  const [areaUnit, setAreaUnit] = useState<AreaUnit>(initialState?.areaUnit ?? sessionState?.areaUnit ?? localeDefaults.area);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(initialState?.weightUnit ?? sessionState?.weightUnit ?? localeDefaults.weight);

  const setCurrencyCode = useCallback((code: string) => {
    setRawCurrencyCode(code);
    window.BayCrewAnalytics?.track('currency_changed', { currency: code });
  }, [setRawCurrencyCode]);

  // Track calculator usage on mount
  useEffect(() => {
    const scenario = initialState ? 'shared_link' : 'default';
    window.BayCrewAnalytics?.track('seed_calculator_used', { scenario });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync country -> currency when country changes (unless URL provided a currency)
  useEffect(() => {
    if (!initialState?.currency) {
      setRawCurrencyCode(country.currencyCode);
    }
  }, [country.currencyCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist guide visibility
  const toggleGuide = useCallback(() => {
    setShowInstructions((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_GUIDE_KEY, String(next));
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, []);

  // Handle crop list changes - sync data records and persist
  const handleCropsChange = useCallback((newCrops: CropConfig[]) => {
    setCrops(newCrops);
    saveCropsToStorage(newCrops);
    const newIds = newCrops.map((c) => c.id);

    setDemoFarm((prev) => {
      const evenPct = Math.floor(100 / newCrops.length);
      const remainder = 100 - evenPct * newCrops.length;
      const cropPercentages: Record<string, number> = {};
      const seedRatesKgPerHa: Record<string, number> = {};
      const seedCostsLocalPerKg: Record<string, number> = {};
      newIds.forEach((id, i) => {
        cropPercentages[id] = prev.cropPercentages[id] ?? (evenPct + (i === 0 ? remainder : 0));
        seedRatesKgPerHa[id] = prev.seedRatesKgPerHa[id] ?? DEFAULT_SEED_RATES[id] ?? 5;
        seedCostsLocalPerKg[id] = prev.seedCostsLocalPerKg[id] ?? DEFAULT_SEED_COSTS[id] ?? 5000;
      });
      return { ...prev, cropPercentages, seedRatesKgPerHa, seedCostsLocalPerKg };
    });

    setTestPackets((prev) => {
      const packetWeightsKg: Record<string, number> = {};
      newIds.forEach((id) => {
        packetWeightsKg[id] = prev.packetWeightsKg[id] ?? DEFAULT_PACKET_WEIGHTS[id] ?? 0.1;
      });
      return { ...prev, packetWeightsKg };
    });

    window.BayCrewAnalytics?.track('crops_changed', { count: newCrops.length, crops: newCrops.map((c) => c.label) });
  }, []);

  // Sync state to URL params (replaceState to avoid history pollution)
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('crops', cropsToParamString(crops));
    params.set('fh', String(demoFarm.farmHectares));
    params.set('cp', cropRecordToString(demoFarm.cropPercentages, cropIds));
    params.set('sr', cropRecordToString(demoFarm.seedRatesKgPerHa, cropIds));
    params.set('sc', cropRecordToString(demoFarm.seedCostsLocalPerKg, cropIds));
    params.set('rh', String(testPackets.refugeeHouseholds));
    params.set('hh', String(testPackets.hostCommunityHouseholds));
    params.set('rp', String(testPackets.avgPacketsPerRefugeeHousehold));
    params.set('hp', String(testPackets.avgPacketsPerHostHousehold));
    params.set('pw', cropRecordToString(testPackets.packetWeightsKg, cropIds));
    params.set('cur', currencyCode);
    params.set('au', areaUnit);
    params.set('wu', weightUnit);
    setSearchParams(params, { replace: true });

    // Also persist to sessionStorage for page refresh resilience
    saveToSession({ crops, demoFarm, testPackets, currencyCode, areaUnit, weightUnit });
  }, [crops, demoFarm, testPackets, currencyCode, areaUnit, weightUnit, cropIds, setSearchParams]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window.BayCrewAnalytics?.track('calculation_shared', { method: 'copy_link' });
    } catch {
      // Clipboard API unavailable
    }
  }, []);

  const results = useMemo((): CalculationResults => {
    const demoFarmResults = cropIds.reduce(
      (acc, crop) => {
        const hectares = demoFarm.farmHectares * ((demoFarm.cropPercentages[crop] ?? 0) / 100);
        const seedKg = hectares * (demoFarm.seedRatesKgPerHa[crop] ?? 0);
        const costLocal = seedKg * (demoFarm.seedCostsLocalPerKg[crop] ?? 0);
        const costUsd = costLocal / exchangeRate;

        acc.seedRequirementsKg[crop] = seedKg;
        acc.hectaresPerCrop[crop] = hectares;
        acc.costsLocal[crop] = costLocal;
        acc.costsUsd[crop] = costUsd;
        acc.totalSeedKg += seedKg;
        acc.totalCostLocal += costLocal;
        acc.totalCostUsd += costUsd;

        return acc;
      },
      {
        seedRequirementsKg: {} as Record<string, number>,
        hectaresPerCrop: {} as Record<string, number>,
        costsLocal: {} as Record<string, number>,
        costsUsd: {} as Record<string, number>,
        totalSeedKg: 0,
        totalCostLocal: 0,
        totalCostUsd: 0,
      }
    );

    const totalTestPackets =
      testPackets.refugeeHouseholds * testPackets.avgPacketsPerRefugeeHousehold +
      testPackets.hostCommunityHouseholds * testPackets.avgPacketsPerHostHousehold;

    const testPacketsResults = cropIds.reduce(
      (acc, crop) => {
        const seedKg = totalTestPackets * (testPackets.packetWeightsKg[crop] ?? 0);
        const equivalentHa = seedKg / (demoFarm.seedRatesKgPerHa[crop] || 1);
        const costLocal = seedKg * (demoFarm.seedCostsLocalPerKg[crop] ?? 0);
        const costUsd = costLocal / exchangeRate;

        acc.seedRequirementsKg[crop] = seedKg;
        acc.equivalentHectares[crop] = equivalentHa;
        acc.costsLocal[crop] = costLocal;
        acc.costsUsd[crop] = costUsd;
        acc.totalSeedKg += seedKg;
        acc.totalCostLocal += costLocal;
        acc.totalCostUsd += costUsd;

        return acc;
      },
      {
        seedRequirementsKg: {} as Record<string, number>,
        equivalentHectares: {} as Record<string, number>,
        costsLocal: {} as Record<string, number>,
        costsUsd: {} as Record<string, number>,
        totalTestPackets,
        totalSeedKg: 0,
        totalCostLocal: 0,
        totalCostUsd: 0,
      }
    );

    return {
      demoFarm: demoFarmResults,
      testPackets: testPacketsResults,
      combined: {
        totalSeedKg: demoFarmResults.totalSeedKg + testPacketsResults.totalSeedKg,
        totalCostLocal: demoFarmResults.totalCostLocal + testPacketsResults.totalCostLocal,
        totalCostUsd: demoFarmResults.totalCostUsd + testPacketsResults.totalCostUsd,
      },
    };
  }, [cropIds, demoFarm, testPackets, exchangeRate]);

  return (
    <div className="space-y-4">
      {/* Compact header toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="text-xl md:text-2xl font-display text-spi-green-dark whitespace-nowrap">
            {t('calculator.title')}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleGuide}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200 text-xs font-medium hover:bg-blue-100 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {showInstructions ? t('calculator.hideGuide') : t('calculator.guide')}
          </button>
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-spi-green/10 text-spi-green-dark border border-spi-green/20 text-xs font-medium hover:bg-spi-green/20 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
            {copied ? t('calculator.copied') : t('calculator.share')}
          </button>
        </div>
      </div>

      {showInstructions && (
        <Instructions
          currencyCode={currencyCode}
          exchangeRate={exchangeRate}
          areaUnit={areaUnit}
          weightUnit={weightUnit}
          demoFarm={demoFarm}
          testPackets={testPackets}
          crops={crops}
        />
      )}

      <CropSetup crops={crops} onChange={handleCropsChange} />

      <DemoFarmSection
        inputs={demoFarm}
        onChange={setDemoFarm}
        results={results.demoFarm}
        crops={crops}
        currencyCode={currencyCode}
        onCurrencyChange={setCurrencyCode}
        exchangeRate={exchangeRate}
        isLive={isLive}
        isLoading={isLoading}
        areaUnit={areaUnit}
        onAreaUnitChange={setAreaUnit}
        weightUnit={weightUnit}
        onWeightUnitChange={setWeightUnit}
      />
      <TestPacketsSection
        inputs={testPackets}
        onChange={setTestPackets}
        results={results.testPackets}
        seedCosts={demoFarm.seedCostsLocalPerKg}
        seedRates={demoFarm.seedRatesKgPerHa}
        crops={crops}
        currencyCode={currencyCode}
        areaUnit={areaUnit}
        weightUnit={weightUnit}
      />

      <ResultsSummary results={results} crops={crops} currencyCode={currencyCode} weightUnit={weightUnit} />
    </div>
  );
}
